// pothos manager «Команда» v2 (хэндофф 2026-07-19) — /api/getManagerTeamBoard.
// Все производственные цифры из каталога pothos_assets (dept_*-колонки),
// пометки менеджера из pothos_team_notes. Категория «Чеки» (receipt) в
// каталоге отсутствует — поле есть в контракте, всегда 0, пока чеки не
// заведены в индексе.
import { z } from 'zod';

export const PothosTeamBoardPeriodSchema = z.enum(['today', '7', '30', 'all']);
export type PothosTeamBoardPeriod = z.infer<typeof PothosTeamBoardPeriodSchema>;

export const PothosTeamCatCountsSchema = z.object({
  gift: z.number(),
  member: z.number(),
  coupon: z.number(),
  ticket: z.number(),
  receipt: z.number(),
});
export type PothosTeamCatCounts = z.infer<typeof PothosTeamCatCountsSchema>;

export const PothosUpworkRowSchema = z.object({
  person: z.string(),
  country_iso: z.string(),
  device: z.string(),
  received: z.number(),
  accepted: z.number(),
  review: z.number(),
  rejected: z.number(),
  checks: z.number(),
  cats: PothosTeamCatCountsSchema,
  grp: z.string(),
  note: z.string(),
  task: z.string(),
  last_at: z.string(),
  reject_reasons: z.array(z.object({ reason: z.string(), n: z.number() })),
});
export type PothosUpworkRow = z.infer<typeof PothosUpworkRowSchema>;

export const PothosTeamBoardPersonSchema = z.object({
  name: z.string(),
  total: z.number(),
  accepted: z.number(),
  week: z.number(),
});
export type PothosTeamBoardPerson = z.infer<typeof PothosTeamBoardPersonSchema>;

export const PothosTeamBoardDeptSchema = z.object({
  key: z.string(),
  people: z.array(PothosTeamBoardPersonSchema),
  total: z.number(),
  accepted: z.number(),
  week: z.number(),
});
export type PothosTeamBoardDept = z.infer<typeof PothosTeamBoardDeptSchema>;

export const PothosScraperRowSchema = z.object({
  person: z.string(),
  received: z.number(),
  accepted: z.number(),
  cats: PothosTeamCatCountsSchema,
  countries: z.array(z.object({
    iso: z.string(),
    gift: z.number(),
    coupon: z.number(),
    receipt: z.number(),
    ticket: z.number(),
    total: z.number(),
  })),
});
export type PothosScraperRow = z.infer<typeof PothosScraperRowSchema>;

export const PothosTeamTaskSchema = z.object({
  id: z.string(),
  dept: z.string(),
  locale_code: z.string(),
  lang: z.string(),
  title: z.string(),
  descr: z.string(),
  deadline: z.string(),
  status: z.string(),
});
export type PothosTeamTask = z.infer<typeof PothosTeamTaskSchema>;

export const PothosTeamTaskSetReqSchema = z.object({
  id: z.string().max(64).optional(),
  dept: z.string().trim().min(1).max(24),
  locale_code: z.string().trim().max(12).default(''),
  lang: z.string().trim().max(4).default(''),
  title: z.string().trim().min(1).max(160),
  descr: z.string().max(2000).default(''),
  deadline: z.string().max(20).default(''),
  status: z.enum(['open', 'done', 'cancelled']).default('open'),
});
export type PothosTeamTaskSetReq = z.infer<typeof PothosTeamTaskSetReqSchema>;
export const PothosTeamTaskSetResSchema = z.object({ ok: z.boolean(), id: z.string() });

export const PothosManagerUploadForReqSchema = z.object({
  person: z.string().trim().min(1).max(160),
  category: z.enum(['gift_card', 'event_ticket', 'coupon', 'membership']),
  modality: z.enum(['photo', 'screenshot']),
  // Plan locale_code (de_DE / th_TH / …) — required so every manager upload
  // enters the QA queue with packaging-vocabulary locale+country already set.
  locale: z.string().trim().min(4).max(12),
  merchant: z.string().trim().max(80).default(''),
  file_name: z.string().max(200).default(''),
  mime: z.string().max(60).default('image/jpeg'),
  file_b64: z.string().min(1).max(12_000_000),
  // Image pixel dims (client-decoded) — feed the packaging deviceGuess.
  width: z.number().int().min(0).max(20000).default(0),
  height: z.number().int().min(0).max(20000).default(0),
  // Optional background-group token: frames sharing it collapse into one
  // participant at packaging finalize (same rule as work capture batches).
  group: z.string().trim().max(80).default(''),
});
export type PothosManagerUploadForReq = z.infer<typeof PothosManagerUploadForReqSchema>;
export const PothosManagerUploadForResSchema = z.object({
  ok: z.boolean(),
  id: z.string().default(''),
  duplicate: z.boolean().default(false),
  // '' | 'no_code' | 'duplicate_code' | 'merchant_full' | 'unknown_locale'
  reason: z.string().default(''),
});
export type PothosManagerUploadForRes = z.infer<typeof PothosManagerUploadForResSchema>;

// Per-designer сводка для страницы «Дизайнеры» в internal: всего сделал,
// принято, на переделке СЕЙЧАС (rejected, ещё не перезалито), на QA, за неделю.
export const PothosDesignerRowSchema = z.object({
  name: z.string(),
  total: z.number(),
  accepted: z.number(),
  rework: z.number(),
  review: z.number(),
  week: z.number(),
  last_at: z.string().default(''),
});
export type PothosDesignerRow = z.infer<typeof PothosDesignerRowSchema>;

export const PothosManagerTeamBoardResSchema = z.object({
  period: PothosTeamBoardPeriodSchema,
  upwork: z.array(PothosUpworkRowSchema),
  depts: z.array(PothosTeamBoardDeptSchema),
  designers: z.array(PothosDesignerRowSchema).default([]),
  designer_sources_available: z.number(),
  scrapers: z.array(PothosScraperRowSchema),
  qa: z.object({ checked: z.number(), accepted: z.number(), rejected: z.number(), person: z.string() }),
  prod: z.object({ total: z.number(), accepted: z.number(), week: z.number() }),
  tasks: z.array(PothosTeamTaskSchema),
  task_locales: z.array(z.string()),
  mgmt: z.object({ depts: z.number(), people: z.number(), shipments: z.number() }),
});
export type PothosManagerTeamBoardRes = z.infer<typeof PothosManagerTeamBoardResSchema>;

export const PothosSetTeamNoteReqSchema = z.object({
  dept: z.string().trim().min(1).max(24),
  person: z.string().trim().min(1).max(160),
  note: z.string().max(2000).optional(),
  grp: z.enum(['', 'active', 'receipt', 'assign', 'dropped']).optional(),
  task: z.string().max(200).optional(),
  device: z.enum(['', 'ios', 'android', 'non']).optional(),
});
export type PothosSetTeamNoteReq = z.infer<typeof PothosSetTeamNoteReqSchema>;
export const PothosSetTeamNoteResSchema = z.object({ ok: z.boolean() });
